import React, { useCallback, useMemo, useState } from 'react';
import { Download, Loader2, Trash2 } from 'lucide-react';
import {
  hasPrePortalPackages,
  isQuotePlaceholderPackage,
  PRE_PORTAL_RELEASE_ID,
  releaseVersionLabel,
} from '../../utils/timberRfqPrePortalVersion';
import { CNC_QUOTE_PLACEHOLDER_LABEL, hasQuotePlaceholderPackages } from '../../utils/combinedQuoteSplit';
import { deleteRfqHubFileRelease } from '../../utils/rfqHubFileReleaseService';
import { filterTargetsWithoutHubReleasePackage } from '../../utils/vendorPackageVersion';
import type { RfqHubFileRelease, RfqPackage, RfqProject } from '../../types';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import RfqReleaseDisplayLabelEditor from './RfqReleaseDisplayLabelEditor';
import { RfqReleasePurposeBadge, RfqReleaseStructureChips } from './RfqReleaseScopeBadges';
import RfqVersionTimelineNode from './RfqVersionTimelineNode';
import './TimberRfqReleasePanel.css';

export interface CncRfqReleasePanelProps {
  project: RfqProject;
  publishTargetOrgIds: string[];
  releases: RfqHubFileRelease[];
  packages: RfqPackage[];
  selectedReleaseId: string;
  onSelectedReleaseChange: (releaseId: string) => void;
  /** Release ids that still have an approved quote (show Approved badge). */
  awardedReleaseIds?: Set<string>;
  /** Awarded suppliers — the only targets for reference releases (falls back to all targets). */
  referenceTargetOrgIds?: string[];
  publishBusy: boolean;
  canPublish: boolean;
  onPublish?: (releaseId: string) => void;
  onRequestPublishReview?: (release: RfqHubFileRelease) => void;
  onSyncHubLink: (proj: RfqProject) => void;
}

const fmtReleaseDate = (d?: Date): string => {
  if (!(d instanceof Date)) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
};

const CncRfqReleasePanel: React.FC<CncRfqReleasePanelProps> = ({
  project,
  publishTargetOrgIds,
  releases,
  packages,
  selectedReleaseId,
  onSelectedReleaseChange,
  awardedReleaseIds,
  referenceTargetOrgIds,
  publishBusy,
  canPublish,
  onPublish,
  onRequestPublishReview,
  onSyncHubLink,
}) => {
  const [removingReleaseId, setRemovingReleaseId] = useState<string | null>(null);

  const sortedReleases = useMemo(
    () => [...releases].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex),
    [releases],
  );

  const showQuotePlaceholderVersion = useMemo(
    () => hasQuotePlaceholderPackages(packages),
    [packages],
  );
  const showLegacyPrePortalVersion = useMemo(
    () =>
      hasPrePortalPackages(packages)
      && packages.some((p) => !isQuotePlaceholderPackage(p) && (p.legacyImport || p.releaseVersionIndex === 0)),
    [packages],
  );
  const showPrePortalVersion = showQuotePlaceholderVersion || showLegacyPrePortalVersion;

  const publishedReleaseIds = useMemo(() => {
    const ids = new Set<string>();
    packages.forEach((p) => {
      if (p.hubFileReleaseId) ids.add(p.hubFileReleaseId);
    });
    return ids;
  }, [packages]);

  const handleRemoveRelease = useCallback(
    async (release: RfqHubFileRelease) => {
      if (!release.id || publishedReleaseIds.has(release.id)) return;
      const versionLabel = releaseVersionLabel(release.releaseVersionIndex);
      if (
        !window.confirm(
          `Remove staged release ${versionLabel}? The Project Hub file will stay; only this supplier staging copy is deleted.`,
        )
      ) {
        return;
      }
      setRemovingReleaseId(release.id);
      try {
        await deleteRfqHubFileRelease(release);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Could not remove staged release.');
      } finally {
        setRemovingReleaseId(null);
      }
    },
    [publishedReleaseIds],
  );

  if (sortedReleases.length === 0 && !showPrePortalVersion) {
    return (
      <div className="srfq-timber-releases">
        <p className="srfq-timber-releases-empty">
          No staged 3D models yet — release from Project Hub → Files (3D track).
        </p>
        {project.timberProjectId ? (
          <p className="srfq-timber-releases-empty">
            Linked Project Hub job:{' '}
            <strong>{project.timberProjectName || project.timberProjectId}</strong>
          </p>
        ) : (
          <p className="srfq-timber-releases-empty">
            No Project Hub job linked for this deal yet.{' '}
            <button type="button" className="srfq-linkish" onClick={() => onSyncHubLink(project)}>
              Try to link Project Hub now
            </button>
          </p>
        )}
      </div>
    );
  }

  const prePortalOnly = showPrePortalVersion && sortedReleases.length === 0;

  return (
    <div className="srfq-timber-releases">
      <ul className="srfq-timber-version-list srfq-timber-version-list--timeline" aria-label="Released CNC models">
        {sortedReleases.length > 0 && (
          <li className="srfq-timber-version-item srfq-timber-version-item--section">
            <div className="srfq-timber-version-track" aria-hidden />
            <p className="srfq-timber-version-group-label">Staged releases</p>
          </li>
        )}

        {sortedReleases.map((release, idx) => {
          const isSelected = release.id === selectedReleaseId;
          const isPublished = release.id ? publishedReleaseIds.has(release.id) : false;
          const releaseTargets =
            isReferenceRelease(release) && referenceTargetOrgIds?.length
              ? referenceTargetOrgIds
              : publishTargetOrgIds;
          const pendingPublishTargets =
            release.id && project.id
              ? filterTargetsWithoutHubReleasePackage(
                  packages,
                  project.id,
                  release.id,
                  releaseTargets,
                )
              : releaseTargets;
          const hasPendingPublish = pendingPublishTargets.length > 0;
          const quoteRequestsFullySent = isPublished && !hasPendingPublish;
          const canPublishRelease =
            canPublish && !!release.id && !publishBusy && hasPendingPublish;
          const isFirstNode = idx === 0;
          const isLastNode = !showPrePortalVersion && idx === sortedReleases.length - 1;
          const versionInactive = sortedReleases.length > 1 && idx > 0;
          const isReference = isReferenceRelease(release);
          const supplierNoun = (n: number) => `${n} supplier${n === 1 ? '' : 's'}`;
          const publishIdleLabel = isReference
            ? isPublished && hasPendingPublish
              ? `Share with ${supplierNoun(pendingPublishTargets.length)}`
              : 'Share Reference Release'
            : isPublished && hasPendingPublish
              ? `Send to ${supplierNoun(pendingPublishTargets.length)}`
              : 'Send Quote Requests';
          const publishSentLabel = isReference ? 'Shared with suppliers' : 'Quote requests sent';
          const publishTitle = quoteRequestsFullySent
            ? isReference
              ? 'This reference release was already shared with every awarded supplier.'
              : 'Quote request emails were already sent to all suppliers for this release.'
            : isPublished && hasPendingPublish
              ? isReference
                ? `Share this reference release with ${supplierNoun(pendingPublishTargets.length)} not yet on it. They will not be asked to requote.`
                : `Send quote requests to ${supplierNoun(pendingPublishTargets.length)} not yet on this release.`
              : isReference
                ? 'Suppliers receive the refined file to cut from. No quote is requested.'
                : undefined;

          return (
            <li
              key={release.id}
              className={`srfq-timber-version-item${isFirstNode ? ' srfq-timber-version-item--first-node' : ''}${
                isLastNode ? ' srfq-timber-version-item--last-node' : ''
              }${isLastNode ? ' srfq-timber-version-item--last' : ''}${
                versionInactive ? ' srfq-timber-version-item--inactive' : ''
              }`}
            >
              <div className="srfq-timber-version-track" aria-hidden>
                <RfqVersionTimelineNode selected={isSelected} />
              </div>
              <div
                className={`srfq-timber-version-row${isSelected ? ' srfq-timber-version-row--active' : ''}${
                  isReference ? ' srfq-timber-version-row--reference' : ''
                }`}
              >
                <div
                  className="srfq-timber-version-select"
                  aria-pressed={isSelected}
                  onClick={() => release.id && onSelectedReleaseChange(release.id)}
                >
                  <span className="srfq-timber-version-main">
                    <span className="srfq-timber-version-title">
                      {releaseVersionLabel(release.releaseVersionIndex)}
                      <RfqReleaseDisplayLabelEditor
                        release={release}
                        packages={packages}
                        isPublished={isPublished}
                      />
                      {idx === 0 && <span className="srfq-timber-version-badge">Latest</span>}
                      <RfqReleasePurposeBadge releasePurpose={release.releasePurpose} quoteSilent />
                      {release.id && awardedReleaseIds?.has(release.id) && (
                        <span className="srfq-timber-version-badge srfq-timber-version-badge--approved">
                          Approved
                        </span>
                      )}
                      {isPublished ? (
                        <span className="srfq-timber-version-badge srfq-timber-version-badge--published">
                          {isReference ? 'Shared' : 'Published'}
                        </span>
                      ) : (
                        <span className="srfq-timber-version-badge srfq-timber-version-badge--unpublished">
                          {isReference ? 'Not shared' : 'Unpublished'}
                        </span>
                      )}
                    </span>
                    <span className="srfq-timber-version-meta">
                      {fmtReleaseDate(release.createdAt instanceof Date ? release.createdAt : undefined)}
                      {release.releasedByName ? ` · ${release.releasedByName}` : ''}
                      {isReference && (
                        <span className="srfq-release-reference-note">
                          Refined model for the approved quote — suppliers cut from this, they do not requote it.
                        </span>
                      )}
                    </span>
                    <RfqReleaseStructureChips structureNames={release.structureNames} />
                  </span>
                </div>
                <div className="srfq-timber-version-actions">
                  <a
                    className="srfq-timber-version-download"
                    href={release.releasedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Download ${release.fileName}`}
                  >
                    <Download size={13} aria-hidden />
                  </a>
                  {!isPublished && (
                    <button
                      type="button"
                      className="srfq-timber-version-remove"
                      onClick={() => void handleRemoveRelease(release)}
                      disabled={removingReleaseId === release.id || publishBusy}
                      aria-label={`Remove ${releaseVersionLabel(release.releaseVersionIndex)}`}
                      title="Remove staged release"
                    >
                      {removingReleaseId === release.id ? (
                        <Loader2 size={13} className="srfq-timber-spin" aria-hidden />
                      ) : (
                        <Trash2 size={13} aria-hidden />
                      )}
                    </button>
                  )}
                  {isSelected && (
                    <button
                      type="button"
                      className={`ops-new-btn srfq-timber-version-publish${
                        quoteRequestsFullySent ? ' srfq-timber-version-publish--sent' : ''
                      }`}
                      disabled={!canPublishRelease}
                      title={publishTitle}
                      onClick={() => {
                        if (!release.id || quoteRequestsFullySent || !hasPendingPublish) return;
                        if (onRequestPublishReview) {
                          onRequestPublishReview(release);
                        } else if (onPublish) {
                          onPublish(release.id);
                        }
                      }}
                    >
                      {publishBusy ? 'Sending…' : quoteRequestsFullySent ? publishSentLabel : publishIdleLabel}
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}

        {showPrePortalVersion && (
          <>
            {(sortedReleases.length > 0 || prePortalOnly) && (
              <li
                className={`srfq-timber-version-item srfq-timber-version-item--section${
                  sortedReleases.length > 0 ? ' srfq-timber-version-item--section-pre-portal' : ''
                }`}
              >
                <div className="srfq-timber-version-track" aria-hidden />
                <p className="srfq-timber-version-group-label">
                  {showQuotePlaceholderVersion && !showLegacyPrePortalVersion
                    ? 'Quote without 3D'
                    : showQuotePlaceholderVersion
                      ? 'Quote without 3D / Pre-portal'
                      : 'Pre-portal imports'}
                </p>
              </li>
            )}
            <li
              className={`srfq-timber-version-item srfq-timber-version-item--last srfq-timber-version-item--last-node${
                sortedReleases.length === 0 ? ' srfq-timber-version-item--first-node' : ''
              }${sortedReleases.length > 0 ? ' srfq-timber-version-item--inactive' : ''}`}
            >
              <div className="srfq-timber-version-track" aria-hidden>
                <RfqVersionTimelineNode
                  selected={selectedReleaseId === PRE_PORTAL_RELEASE_ID}
                  prePortal
                />
              </div>
              <div
                className={`srfq-timber-version-row srfq-timber-version-row--pre-portal${
                  selectedReleaseId === PRE_PORTAL_RELEASE_ID
                    ? ' srfq-timber-version-row--active'
                    : ''
                }`}
              >
                <button
                  type="button"
                  className="srfq-timber-version-select"
                  onClick={() => onSelectedReleaseChange(PRE_PORTAL_RELEASE_ID)}
                  aria-pressed={selectedReleaseId === PRE_PORTAL_RELEASE_ID}
                >
                  <span className="srfq-timber-version-main">
                    <span className="srfq-timber-version-title">
                      {releaseVersionLabel(0, { quotePlaceholder: showQuotePlaceholderVersion && !showLegacyPrePortalVersion })}
                      <span className="srfq-timber-version-label">
                        {showQuotePlaceholderVersion
                          ? CNC_QUOTE_PLACEHOLDER_LABEL
                          : 'Imported quotes'}
                      </span>
                      {sortedReleases.length === 0 && (
                        <span className="srfq-timber-version-badge">Latest</span>
                      )}
                      {awardedReleaseIds?.has(PRE_PORTAL_RELEASE_ID) && (
                        <span className="srfq-timber-version-badge srfq-timber-version-badge--approved">
                          Approved
                        </span>
                      )}
                      <span className="srfq-timber-version-badge srfq-timber-version-badge--pre-portal">
                        {showQuotePlaceholderVersion && !showLegacyPrePortalVersion
                          ? 'Quote only'
                          : 'Pre-portal'}
                      </span>
                    </span>
                    <span className="srfq-timber-version-meta">
                      {showQuotePlaceholderVersion
                        ? 'CNC portion split from a combined quote before a 3D model was staged'
                        : 'Quotes received before the supplier portal existed'}
                    </span>
                  </span>
                </button>
              </div>
            </li>
          </>
        )}
      </ul>
    </div>
  );
};

export default CncRfqReleasePanel;
